/**
 * Approval Inbox — approvers see pending edit requests here.
 * Roles are matched to entity types via `edit_approval_rules`.
 */
import { useState } from 'react'
import {
  Tabs, Table, Tag, Button, Space, Typography, App, Modal, Input, Empty,
} from 'antd'
import {
  CheckOutlined, CloseOutlined, ClockCircleOutlined, InboxOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { editLockApi } from '@/features/editLock/api'
import PageHeader from '@/shared/components/PageHeader'
import type { EditRequest } from '@/shared/types/editLock'

const { Text } = Typography

const STATUS_COLORS: Record<string, string> = {
  PENDING:   'orange',
  APPROVED:  'green',
  REJECTED:  'red',
  EXPIRED:   'default',
  COMPLETED: 'blue',
}

function formatEntity(name: string) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function ApprovalInboxPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()

  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['edit-requests-pending'],
    queryFn: editLockApi.listPending,
    refetchInterval: 60_000,
  })

  const { data: mine = [], isLoading: loadingMine } = useQuery({
    queryKey: ['edit-requests-mine'],
    queryFn: () => editLockApi.listMine(),
  })

  const [actionTarget, setActionTarget] = useState<{ req: EditRequest; status: 'APPROVED' | 'REJECTED' } | null>(null)
  const [remarks, setRemarks] = useState('')

  const actMutation = useMutation({
    mutationFn: () => editLockApi.actOnRequest(actionTarget!.req.id, actionTarget!.status, remarks),
    onSuccess: () => {
      message.success(actionTarget!.status === 'APPROVED' ? 'Edit approved' : 'Edit rejected')
      qc.invalidateQueries({ queryKey: ['edit-requests-pending'] })
      setActionTarget(null)
      setRemarks('')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Action failed'),
  })

  const pendingColumns = [
    {
      title: 'Entity', dataIndex: 'entity_name', key: 'entity_name',
      render: (v: string) => <Text strong>{formatEntity(v)}</Text>,
    },
    { title: 'Record ID', dataIndex: 'entity_id', key: 'entity_id',
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v.slice(0, 8)}…</Text> },
    { title: 'Requested By', dataIndex: 'requested_by_name', key: 'requested_by_name' },
    { title: 'Reason', dataIndex: 'reason', key: 'reason',
      render: (v: string) => <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span> },
    { title: 'Requested At', dataIndex: 'requested_at', key: 'requested_at',
      render: (v: string) => new Date(v).toLocaleString() },
    {
      title: 'Action', key: 'action',
      render: (_: unknown, r: EditRequest) => (
        <Space>
          <Button
            size="small" type="primary" icon={<CheckOutlined />}
            onClick={() => { setActionTarget({ req: r, status: 'APPROVED' }); setRemarks('') }}
          >
            Approve
          </Button>
          <Button
            size="small" danger icon={<CloseOutlined />}
            onClick={() => { setActionTarget({ req: r, status: 'REJECTED' }); setRemarks('') }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ]

  const myColumns = [
    { title: 'Entity', dataIndex: 'entity_name', key: 'entity_name',
      render: (v: string) => formatEntity(v) },
    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
    { title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{s}</Tag> },
    { title: 'Approver', dataIndex: 'approved_by_name', key: 'approved_by_name',
      render: (v?: string) => v ?? '—' },
    { title: 'Remarks', dataIndex: 'approval_remarks', key: 'approval_remarks',
      render: (v?: string) => v ?? '—' },
    { title: 'Time Remaining', key: 'remaining',
      render: (_: unknown, r: EditRequest) =>
        r.status === 'APPROVED' && r.seconds_remaining != null
          ? <Tag icon={<ClockCircleOutlined />} color="gold">
              {Math.floor(r.seconds_remaining / 60)} min left
            </Tag>
          : '—',
    },
    { title: 'Requested At', dataIndex: 'requested_at', key: 'requested_at',
      render: (v: string) => new Date(v).toLocaleString() },
  ]

  return (
    <div>
      <PageHeader
        title="Approval Inbox"
        subtitle="Edit-request approvals and your own pending requests"
      />

      <Tabs
        defaultActiveKey="pending"
        items={[
          {
            key: 'pending',
            label: (
              <span>
                <InboxOutlined /> Pending My Approval
                {pending.length > 0 && <Tag color="orange" style={{ marginLeft: 8 }}>{pending.length}</Tag>}
              </span>
            ),
            children: (
              <Table
                rowKey="id"
                columns={pendingColumns}
                dataSource={pending}
                loading={loadingPending}
                locale={{ emptyText: <Empty description="No pending edit requests" /> }}
                size="small"
              />
            ),
          },
          {
            key: 'mine',
            label: <span>My Requests</span>,
            children: (
              <Table
                rowKey="id"
                columns={myColumns}
                dataSource={mine}
                loading={loadingMine}
                locale={{ emptyText: <Empty description="You have no edit requests" /> }}
                size="small"
              />
            ),
          },
        ]}
      />

      <Modal
        title={
          actionTarget?.status === 'APPROVED'
            ? `Approve edit on ${formatEntity(actionTarget?.req.entity_name ?? '')}`
            : `Reject edit on ${formatEntity(actionTarget?.req.entity_name ?? '')}`
        }
        open={!!actionTarget}
        onCancel={() => setActionTarget(null)}
        onOk={() => actMutation.mutate()}
        confirmLoading={actMutation.isPending}
        okButtonProps={{ danger: actionTarget?.status === 'REJECTED' }}
        okText={actionTarget?.status === 'APPROVED' ? 'Approve' : 'Reject'}
      >
        <p>
          <Text strong>Reason given by requester:</Text><br />
          <span style={{ color: '#595959' }}>{actionTarget?.req.reason}</span>
        </p>
        {actionTarget?.status === 'APPROVED' && (
          <p style={{ color: '#1677ff', background: '#e6f4ff', padding: 8, borderRadius: 6 }}>
            ℹ Approved edits have a 3-hour window. After saving, the record will be re-locked.
          </p>
        )}
        <Input.TextArea
          rows={3}
          placeholder="Optional remarks for the requester…"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>
    </div>
  )
}
